/**
 * Drift Map — the private ending. A thin map of the participant's trajectory:
 * what fed them, by rhetorical tactic. Rendered as SVG so it exports cleanly to
 * PNG for documentation.
 */

import { useRef } from 'react';
import { TACTICS, type DriftMap as DriftData } from '@wintermute/orchestrator';
import { TACTIC_COLOR, TACTIC_LABEL } from './tactics.js';
import { downloadSvgAsPng } from '../capture/capture.js';

interface Props {
  drift: DriftData;
}

const W = 720;
const H = 420;

export function DriftMap({ drift }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);

  const rows = TACTICS.map((t) => ({
    tactic: t,
    share: drift.tacticShare[t],
    seconds: drift.tacticDwell[t],
  }));
  const maxShare = Math.max(0.0001, ...rows.map((r) => r.share));

  const barX = 150;
  const barW = W - barX - 90;
  const rowH = 46;
  const top = 120;

  return (
    <div className="ending-panel">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="drift-svg" role="img" aria-label="Your drift map">
        <rect x={0} y={0} width={W} height={H} fill="#05060a" />
        <text x={40} y={50} fill="#e7e9f0" fontSize={26} fontFamily="system-ui" fontWeight={600}>
          Your drift
        </text>
        <text x={40} y={82} fill="#9aa0b4" fontSize={15} fontFamily="system-ui">
          {drift.summary}
        </text>

        {rows.map((r, i) => {
          const y = top + i * rowH;
          const w = (r.share / maxShare) * barW;
          return (
            <g key={r.tactic}>
              <text x={40} y={y + 20} fill="#c7cbd8" fontSize={15} fontFamily="system-ui">
                {TACTIC_LABEL[r.tactic]}
              </text>
              <rect x={barX} y={y} width={barW} height={26} rx={6} fill="rgba(255,255,255,0.06)" />
              <rect x={barX} y={y} width={Math.max(2, w)} height={26} rx={6} fill={TACTIC_COLOR[r.tactic]} />
              <text x={barX + barW + 12} y={y + 20} fill="#9aa0b4" fontSize={13} fontFamily="system-ui">
                {Math.round(r.share * 100)}%
              </text>
            </g>
          );
        })}

        <text x={40} y={H - 20} fill="#6b7185" fontSize={12} fontFamily="system-ui">
          WinterMute · fed to you: {drift.fedToYou.length} · you fed: {drift.youFed.length}
        </text>
      </svg>

      <div className="ending-actions">
        <button
          onClick={() => svgRef.current && downloadSvgAsPng(svgRef.current, `wintermute-drift-${drift.sessionId}.png`)}
        >
          Save drift as image
        </button>
      </div>
    </div>
  );
}
